import { createSelector } from 'reselect';
// Import selectors.
import {
	makeGetNewBusinessApplicationSelector,
} from '@app/redux/selectors'
import {
	getNewBusinessApplicationState,
} from '@app/redux/selectors/base'
// Import states.
import {
	AppState,
	NewBusinessApplicationState,
} from '@app/redux/states';
// Import api models.
import {
	MortgageeRequest,
	MortgageeWithStatus,
	NewBusinessApplication,
} from '@app/models/api';
// Import enumerations.
import {
	RecordStatus,
} from '@app/enumerations';
// Import props.
import {
	OptionalMortgageeProps,
} from '@app/props';


// Get New Business Mortgagees Selector.
const makeGetNewBusinessMortgageesSelector = () => createSelector(
	[makeGetNewBusinessApplicationSelector()],
	(newBusinessApplication: NewBusinessApplication | undefined): MortgageeWithStatus[] => {
		return newBusinessApplication !== undefined
			? newBusinessApplication.mortgagees
			: [];
	}
);

// Get New Business Mortgagee Lienholder Positions In Use Selector.
const makeGetNewBusinessMortgageeLienholderPositionsInUseSelector = () => createSelector(
	[
		(state: AppState, props: OptionalMortgageeProps) => props.mortgageeGuid,
		makeGetNewBusinessMortgageesSelector()
	],
	(mortgageeGuidToExclude: string | undefined, mortgagees: MortgageeWithStatus[]): number[] =>
		mortgagees
			.filter((mortgagee: MortgageeWithStatus) => mortgageeGuidToExclude === undefined || mortgagee.id !== mortgageeGuidToExclude)
			.map<number>((mortagee: MortgageeWithStatus) => mortagee.lienPositionNumber ?? 0)
			.filter((lienPositionNumber: number) => lienPositionNumber !== 0)
);

// Get New Business Mortgagee Emails In Use Selector.
const makeGetNewBusinessMortgageeEmailsInUseSelector = () => createSelector(
	[
		(state: AppState, props: OptionalMortgageeProps) => props.mortgageeGuid,
		makeGetNewBusinessMortgageesSelector()
	],
	(mortgageeGuidToExclude: string | undefined, mortgagees: MortgageeWithStatus[]): string[] =>
		mortgagees
			.filter((mortgagee: MortgageeWithStatus) => mortgageeGuidToExclude === undefined || mortgagee.id !== mortgageeGuidToExclude)
			.map<string>((mortagee: MortgageeWithStatus) => mortagee.email ?? String.empty())
			.filter((email: string) => !String.isNullOrWhiteSpace(email))
);

// Get Selected New Business Mortgagee Guid Selector.
const makeGetSelectedNewBusinessMortgageeGuidSelector = () => createSelector(
	[getNewBusinessApplicationState],
	(newBusinessApplicationState: NewBusinessApplicationState | undefined): string | undefined => {
		return newBusinessApplicationState?.selectedMortgageeGuid;
	}
);

// Get Selected New Business Mortgagee Selector.
const makeGetSelectedNewBusinessMortgageeSelector = () => createSelector(
	[makeGetSelectedNewBusinessMortgageeGuidSelector(), makeGetNewBusinessMortgageesSelector()],
	(selectedMortgageeGuid: string | undefined, mortgagees: MortgageeWithStatus[]): MortgageeWithStatus | undefined => {
		return !String.isNullOrWhiteSpace(selectedMortgageeGuid)
			? mortgagees.find((mortgagee: MortgageeWithStatus) => mortgagee.id === selectedMortgageeGuid)
			: undefined;
	}
);

// Get Selected New Business Mortgagee As Request Selector.
const makeGetSelectedNewBusinessMortgageeAsRequestSelector = () => createSelector(
	[makeGetSelectedNewBusinessMortgageeSelector()],
	(selectedMortgagee: MortgageeWithStatus | undefined): MortgageeRequest | undefined => {
		let selectedMortgageeRequest: MortgageeRequest | undefined = undefined;
		if (selectedMortgagee !== undefined) {
			selectedMortgageeRequest = new MortgageeRequest();
			selectedMortgageeRequest.name = selectedMortgagee?.name ?? null;
			selectedMortgageeRequest.includeSuccessorOrAssigns = selectedMortgagee?.includeItsSuccessorsAndOrAssigns ?? null;
			selectedMortgageeRequest.includeInterests = selectedMortgagee?.includeAsTheirInterestsMayAppear ?? null;
			selectedMortgageeRequest.loanNumber = selectedMortgagee?.loanNumber ?? null;
			selectedMortgageeRequest.positionNumber = selectedMortgagee?.lienPositionNumber ?? null;
			selectedMortgageeRequest.isMortgageeBilled = selectedMortgagee?.billMortgagee ?? null;
			selectedMortgageeRequest.isMortgageeRequired = selectedMortgagee?.coverageRequiredByMortgagee ?? null;
			selectedMortgageeRequest.address = selectedMortgagee?.mailingAddress ?? null;
			selectedMortgageeRequest.email = selectedMortgagee?.email ?? null;
			selectedMortgageeRequest.sendPaymentReceiptToMortgagee = selectedMortgagee?.sendPaymentReceiptToEmail ?? null;
		}
		return selectedMortgageeRequest;
	}
);

// Are All New Business Mortgagees Complete Selector.
const makeAreAllNewBusinessMortgageesCompleteSelector = () => createSelector(
	[makeGetNewBusinessMortgageesSelector()],
	(mortgagees: MortgageeWithStatus[]): boolean => {
		return mortgagees.every((mortgagee: MortgageeWithStatus) => {
			return mortgagee.status?.statusCode === RecordStatus.COMPLETE
		})
	}
);

// Are New Business Mortgagees Empty Selector.
const makeAreNewBusinessMortgageesEmptySelector = () => createSelector(
	[makeGetNewBusinessMortgageesSelector()],
	(mortgagees: MortgageeWithStatus[]): boolean => {
		return mortgagees.length === 0;
	}
);


// Export composed methods.
export {
	
};

// export selectors.
export {
	makeGetNewBusinessMortgageesSelector,
	makeGetNewBusinessMortgageeLienholderPositionsInUseSelector,
	makeGetNewBusinessMortgageeEmailsInUseSelector,
	makeGetSelectedNewBusinessMortgageeGuidSelector,
	makeGetSelectedNewBusinessMortgageeSelector,
	makeGetSelectedNewBusinessMortgageeAsRequestSelector,
	makeAreAllNewBusinessMortgageesCompleteSelector,
	makeAreNewBusinessMortgageesEmptySelector,
};